import { flagOn } from './attendee-flags';

const PRINTER_USER = '3O7PfMm5hqJJjT5YeFffoeTlf1Hm1mPICV41oyRNKzY';
const GENERATE_LABEL_SECRET =
  'H8WqHkDo2ncRDirAZeBjlBys1M9gtq4963tekOEnO4sukdta3U';

export const printLabel = async (s3LabelUrl: string, printerId: number) => {
  await fetch('https://api.printnode.com/printjobs', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Basic ${btoa(PRINTER_USER)}:`,
    },
    body: JSON.stringify({
      printer: printerId,
      expireAfter: 60,
      title: 'VBS-Name-Label',
      contentType: 'pdf_uri',
      content: s3LabelUrl,
      source: 'CheckInApp',
      color: false,
      qty: 1,
      options: { paper: '30857 Badge Label' },
    }),
  });
};

// Pure body builder for `createLabelPdf`. Extracted so the payload
// shape can be unit-tested without mocking `fetch`.
//
// `customBadges` is rendered onto the printed label by the backend as
// extra colored chips (e.g. group-color tags). The field is omitted from
// the payload entirely when there's nothing to render — the backend
// treats "missing" and "empty array" the same, but omitting keeps the
// request shape minimal and matches the prior behaviour for any
// downstream tools logging the body.
//
// `printGrade` controls whether the backend renders the attendee's grade
// chip on the badge. Only `false` is forwarded (as `printGrade: false`);
// `true` and `undefined` both omit the key, preserving the backend's
// existing default. This mirrors `AppEvent.printGrade` semantics.
export const buildCreateLabelPdfBody = (
  attendeeRef: string,
  customBadges?: string[],
  printGrade?: boolean,
): Record<string, unknown> => {
  const body: Record<string, unknown> = { attendeeId: attendeeRef };
  if (customBadges && customBadges.length > 0) {
    body.customBadges = customBadges;
  }
  if (printGrade === false) {
    body.printGrade = false;
  }
  return body;
};

// Fields the caller supplies when the active event folds the grade into the
// class line. `medicalConcerns` / `foodAllergies` take the attendee's raw
// field values — the flag gating happens in the builder below.
export type InlineLabelInput = {
  firstName: string;
  lastName: string;
  groupName: string;
  teacherNames?: string;
  medicalConcerns?: string;
  foodAllergies?: string;
  customBadges?: string[];
};

// Pure body builder for the *inline* `createLabelPdf` call used when
// `AppEvent.foldGradeIntoGroupName` is on.
//
// Why a second builder instead of extending `buildCreateLabelPdfBody`:
// the backend overwrites every inline field from Firestore whenever
// `attendeeId` is present (`VbsController.createLabelPdf`), so a folded
// `groupName` can only reach the renderer if we omit `attendeeId`
// entirely. That also means the backend won't write `s3LabelUrl` back —
// the caller is responsible for caching it via `cacheLabelUrl`.
//
// `grade` is sent as an explicit empty string and `printGrade` as `false`
// so the renderer can't emit a second, smaller grade line below the class
// line — the grade is already baked into `groupName` by
// `formatGroupForLabel`. Either guard alone would do it; both are sent to
// make the intent legible on the wire.
//
// The two health flags go through `flagOn` rather than a plain non-blank
// test because the backend renders its M / F chip for *any* non-blank
// string — which would light up the chip for legacy `'No'` values. Values
// that are "on" are forwarded verbatim (including a legacy `'Yes'`), since
// the backend only cares that the string is non-blank.
export const buildInlineLabelPdfBody = (
  input: InlineLabelInput,
): Record<string, unknown> => {
  const body: Record<string, unknown> = {
    firstName: input.firstName,
    lastName: input.lastName,
    grade: '',
    groupName: input.groupName,
    teacherNames: input.teacherNames ?? '',
    printGrade: false,
  };
  if (flagOn(input.medicalConcerns)) {
    body.medicalConcerns = input.medicalConcerns;
  }
  if (flagOn(input.foodAllergies)) {
    body.foodAllergies = input.foodAllergies;
  }
  if (input.customBadges && input.customBadges.length > 0) {
    body.customBadges = input.customBadges;
  }
  return body;
};

// Shared transport for both `createLabelPdf` payload shapes. Returns the
// parsed `{ s3LabelUrl }` response.
const postCreateLabelPdf = async (body: Record<string, unknown>) => {
  const res = await fetch('https://gfc.busybucket.io/api/createLabelPdf', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-secret': GENERATE_LABEL_SECRET,
    },
    body: JSON.stringify(body),
  });
  return res.json();
};

// Standard path: the backend looks the attendee (and their group) up in
// Firestore, renders the badge, overwrites `{attendeeId}.pdf` in S3, and
// writes the resulting URL back to the attendee doc itself.
export const generateLabelUrl = async (
  attendeeRef: string,
  customBadges?: string[],
  printGrade?: boolean,
) =>
  postCreateLabelPdf(
    buildCreateLabelPdfBody(attendeeRef, customBadges, printGrade),
  );

// Fold path: we supply every field, so the backend does no Firestore read
// and no write-back. It stores the PDF under a fresh UUID and returns the
// URL; persisting that URL is the caller's job (`cacheLabelUrl`).
export const generateInlineLabelUrl = async (input: InlineLabelInput) =>
  postCreateLabelPdf(buildInlineLabelPdfBody(input));
